import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ExternalLink } from 'lucide-react';
import Logo from '@/components/Logo';

const LoginPage = () => {
  const navigate = useNavigate();
  const {
    adminLogin,
    chroLogin,
    loginWithAuth0,
    auth0Config,
    isAuthenticated,
    isAdmin,
    isChro
  } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      if (isAdmin) {
        navigate('/admin');
      } else if (isChro) {
        navigate('/signal-room');
      }
    }
  }, [isAuthenticated, isAdmin, isChro, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Try CHRO login first, then fall back to admin login
      try {
        const result = await chroLogin(formData.email, formData.password);
        toast.success('Welcome back');
        navigate('/signal-room');
        return;
      } catch {
        // CHRO login failed, try admin
      }

      const result = await adminLogin(formData.email, formData.password);
      toast.success('Welcome back');
      const role = result.role;
      if (role === 'admin' || role === 'super_admin' || role === 'operator') {
        navigate('/admin');
      } else {
        navigate('/signal-room');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const auth0Enabled = auth0Config?.auth0_enabled;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1765445666518-0140e26ae920?w=1920&q=80)`,
          filter: 'blur(2px)'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-background/90" />

      <div className="relative z-10 w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Logo size="hero" />
          <p className="text-sm text-muted-foreground mt-2">
            Workforce Transformation Intelligence
          </p>
        </div>

        <Card className="glass-heavy border-border/50">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl">Welcome to reAImagination</CardTitle>
            <CardDescription>
              Sign in to your workspace
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Auth0 SSO Button */}
            {auth0Enabled && (
              <>
                <Button
                  onClick={() => loginWithAuth0('chro')}
                  className="w-full mb-4"
                  variant="secondary"
                  data-testid="auth0-sso-login-btn"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Sign in with SSO
                </Button>

                <div className="relative my-6">
                  <Separator />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
                    or use credentials
                  </span>
                </div>
              </>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full glow-primary"
                disabled={loading}
                data-testid="login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Powered by <span className="font-medium">Shim Partners</span>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
